import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";

function LastRun({ run }) {
  if (!run) return <span>No email has been sent yet.</span>;
  const when = new Date(run.ran_at).toLocaleString();
  const what = {
    disabled: "skipped, the email was off",
    nothing_unmatched: "skipped, no unmatched leads that week",
    already_sent: "skipped, already sent that week",
    done: `sent to ${run.sent} of ${run.recipients} admin(s)`,
  }[run.status] || run.status;
  return <span>Last run {when} ({run.trigger}) for {run.week_start} – {run.week_end}: {what}.</span>;
}

export const DemandEmailControls = ({ canEdit }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => { setData((await api.get("/admin/demand-map/email")).data); }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong");
    } finally { setBusy(""); }
  };
  const toggle = (enabled) => run("toggle", async () => {
    await api.patch("/admin/demand-map/email", { enabled });
    await load();
  }, enabled ? "Weekly demand email on" : "Weekly demand email off");
  const sendNow = () => run("send", async () => {
    await api.post("/admin/demand-map/email/send-now");
    setTimeout(() => { load().catch(() => {}); }, 4000);
  }, "Sending last week's top unmatched areas to admins");

  if (!data) return null;
  return (
    <div className="border-2 border-black bg-zinc-50 p-3" data-testid="demand-email">
      <div className="flex items-center gap-2 flex-wrap">
        <EnvelopeSimple size={14} weight="bold" />
        <div className="label-mono">Weekly &quot;top unmatched areas&quot; email to admins</div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest" data-testid="demand-email-label">{data.enabled ? "On" : "Off"}</span>
          <Switch checked={data.enabled} disabled={!canEdit || busy === "toggle"} onCheckedChange={toggle} data-testid="demand-email-toggle" />
        </div>
      </div>
      <div className="text-[10.5px] text-zinc-600 mt-1">Sent {data.schedule} to every active Super Admin and Admin. Weeks with no unmatched leads are skipped.</div>
      <div className="text-[10.5px] text-zinc-600 mt-1" data-testid="demand-email-last-run"><LastRun run={data.last_run} /></div>
      {canEdit ? (
        <button type="button" onClick={sendNow} disabled={!data.enabled || busy === "send"} data-testid="demand-email-send-now"
                className="mt-2 border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40">
          Send last week&apos;s email now
        </button>
      ) : (
        <div className="text-[10.5px] text-zinc-500 mt-1" data-testid="demand-email-readonly">Only Super Admins and Admins can change this.</div>
      )}
    </div>
  );
};
